import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
  Container,
  Card,
  CardContent,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getArticle,
  useCreateArticle,
  useUpdateArticle,
} from '../queries/articles';

function ArticleFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [content, setContent] = useState('');
  const [published, setPublished] = useState(false);

  const { data: article, isLoading } = useQuery(getArticle(id));
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();

  useEffect(() => {
    if (article) {
      setTitle(article.title ?? '');
      setDate(article.date ? new Date(article.date).toISOString().slice(0, 10) : '');
      setContent(article.content ?? '');
      setPublished(!!article.published);
    }
  }, [article]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!date) {
      toast.error('Date is required');
      return;
    }
    const payload = { title: title.trim(), date, content: content || '', published };
    try {
      if (isEdit) {
        await updateArticle.mutateAsync({ id, data: payload });
        toast.success('Article updated');
      } else {
        await createArticle.mutateAsync({ data: payload });
        toast.success('Article created');
      }
      navigate('/content');
    } catch (err) {
      toast.error(err?.response?.data?.message || (isEdit ? 'Failed to update article' : 'Failed to create article'));
    }
  };

  const saving = createArticle.isPending || updateArticle.isPending;

  if (isEdit && isLoading) {
    return (
      <Container maxWidth='lg' sx={{ my: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ my: 4 }}>
      <Card variant='outlined' sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.06)', p: 2 }}>
        <CardContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            <Link to='/content' style={{ color: '#1976d2' }}>
              ← Back to Content
            </Link>
          </Typography>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
            <Typography variant='h5' fontWeight='bold' textTransform='uppercase'>
              {isEdit ? 'Edit Article' : 'New Article'}
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label='Title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              margin='normal'
            />
            <TextField
              fullWidth
              label='Date'
              type='date'
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              margin='normal'
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label='Content'
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
              minRows={8}
              margin='normal'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  color='primary'
                />
              }
              label='Published'
              sx={{ mt: 1, display: 'block' }}
            />
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button type='submit' variant='contained' disabled={saving}>
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
              <Button component={Link} to='/content' variant='outlined'>
                Cancel
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}

export default ArticleFormPage;
